import React, {
  FunctionComponent,
  useState,
  ChangeEvent,
  useEffect,
  useContext,
} from "react";
import stickybits from "stickybits";
import {
  AppPage,
  FocusableHeader,
  HomeIcon,
  LoadingMask,
} from "../../components";
import { Link } from "react-router-dom";
import routes from "../../app/routes";
import { TextField, Button } from "@cmsgov/ds-medicare-gov";
import { getInNetworkPharmacies, getPharmacies, getPlan } from "../../api";
import { AppStore } from "../../app/store";
import {
  ActionType,
  PharmacyType,
  PharmaciesRequest,
  PharmacyResultsType,
  LocationWithNextRouteInState,
  InNetworkPharmaciesResponse,
} from "../../@types";
import {
  useTranslate,
  IntlContext,
  CONSTANTS,
  parsePlanLongIdIntoUrlPartForApi,
  useYearInTitle,
} from "../../helpers";
import { useLocation } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { buildAddress, updatePharmacyNetworkStatuses } from "./helpers";
import { PharmacySelectionFooter, ResultsContainer } from "./components";
import {
  InNetworkSelectPharmaciesPlanParams,
  PlanDetailsState,
  PlanDetailsStatus,
  SelectPharmaciesPageProps,
  ToggledFrom,
} from "./types";
import { getPlanName } from "../../helpers/planHelpers";
import { Routes } from "../../app";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
  Ga4EventType,
} from "../../app/contexts/Analytics/types";

export const SelectPharmaciesPage: FunctionComponent<SelectPharmaciesPageProps> = ({
  isInNetwork = false,
  match,
}) => {
  const { dispatch, state } = useContext(AppStore.AppContext);
  const [planDetailState, setPlanDetailState] = React.useState<
    PlanDetailsState
  >({
    status: PlanDetailsStatus.None,
    plan: undefined,
  });
  const { plan, status: planStatus } = planDetailState;
  const {
    cwCoverage,
    futureLis,
    lis,
    mailOrderNetworkStatus = { inNetwork: false, preferred: false },
    mbpReferrerUri,
    pharmacies,
    pharmacyAddress,
    pharmacyType,
    planType,
    year,
    zipcode,
  } = state;
  const flags = useFlags();
  const { mctCurrentPlanYear = "" } = flags;
  const DEFAULT_SEARCH_DISTANCE = 10;
  const location = useLocation();
  const [pharmacyNameSearch, setPharmacyNameSearch] = useState("");
  const [previousNameSearch, setPreviousNameSearch] = useState("");
  const [mailOrder, setMailOrder] = useState(
    [PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(pharmacyType)
  );
  const [shouldMailOrderBeInResults, setShouldMailOrderBeInResults] = useState(
    true
  );
  const [addressTextFieldValue, setAddressTextFieldValue] = useState(
    pharmacyAddress || ""
  );
  const [pharmacyResults, setPharmacyResults] = useState<
    PharmacyResultsType | undefined
  >();

  const [page, setPage] = useState(0);
  const [planId, setPlanId] = useState("");
  const [searchDistance, setSearchDistance] = useState(DEFAULT_SEARCH_DISTANCE);
  const [loading, setLoading] = useState(true);
  const [totalPharmacies, setTotalPharmacies] = useState(0);
  const [savedPharmacies, setSavedPharmacies] = useState(pharmacies);
  const [totalPages, setTotalPages] = useState(1);
  const [showMap, setShowMap] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [showOnlyPreferred, setShowOnlyPreferred] = useState(false);
  const [mobileSearchDistance, setMobileSearchDistance] = useState<
    number | undefined
  >();
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const [footerMessage, setFooterMessage] = useState("");
  const [nextRoute, setNextRoute] = useState<typeof location.state>();
  const [nextRouteIsSummaryPage, setNextRouteIsSummaryPage] = useState(false);
  const hasMbpReferrerUri = mbpReferrerUri !== "";
  const pharmacyAnchorTarget = "pharmacy-selection-footer-sticky-tray-heading";

  useEffect(() => {
    showMapBasedOnResize();

    window.addEventListener("resize", showMapBasedOnResize);

    if (isInNetwork) {
      const currentPlanId = parsePlanLongIdIntoUrlPartForApi(
        (match.params as InNetworkSelectPharmaciesPlanParams).id
      );
      setPlanId(currentPlanId);
      if (currentPlanId && planStatus !== PlanDetailsStatus.Loading) {
        fetchPlan(currentPlanId);
      }
    }

    return (): void =>
      window.removeEventListener("resize", showMapBasedOnResize);
  }, []);

  useEffect(() => {
    if (location.state) {
      const locationState = location.state as LocationWithNextRouteInState;
      if ("nextRoute" in locationState) {
        setNextRoute(locationState.nextRoute);
        const nextPath = locationState.nextRoute?.pathname;
        if (nextPath) {
          const isNextRouteHeadingToSummaryPage = nextPath.includes(
            Routes.summary.landingPage
          );
          if (isNextRouteHeadingToSummaryPage) {
            setNextRouteIsSummaryPage(true);
          }
        }
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (pharmacyResults && showMap) {
      stickybits(".SelectPharmaciesPage__pharmaciesMap");
    }
  }, [pharmacyResults, showMap]);

  useEffect(() => {
    setSavedPharmacies(pharmacies);
  }, [pharmacies]);

  const showMapBasedOnResize = (): void => {
    const largeWidthMatch = window.matchMedia("(min-width: 1024px)");

    if (largeWidthMatch.matches) {
      setShowMap(true);
    }
  };

  const lisToSend =
    year !== mctCurrentPlanYear.toString() && futureLis ? futureLis : lis;

  const fetchPlan = async (planId: string): Promise<void> => {
    setPlanDetailState({
      status: PlanDetailsStatus.Loading,
      plan: undefined,
    });
    try {
      const fetchedPlan = await getPlan(
        planId,
        lisToSend ? { lis: lisToSend } : {}
      );
      setPlanDetailState({
        status: PlanDetailsStatus.Success,
        plan: fetchedPlan,
      });
    } catch (err) {
      setPlanDetailState({
        status: PlanDetailsStatus.Error,
        plan: undefined,
        error: err as Error,
      });
    }
  };

  const sendFindPharmacyGa4Event = (totalPharmacies: number) => {
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.PHARMACY_FIND,
        event_type: Ga4EventType.UI_INTERACTION,
        text: "Find Pharmacy",
        search_result_count: totalPharmacies,
        mct_plan_type: planType,
        mct_medigap: false,
        mct_coverage_wizard: !!cwCoverage,
      },
    });
  };

  const loadPharmacies = async (
    params: {
      radius_miles?: number;
      pageNumber?: number;
      calculateAverageDistance?: boolean;
      filterByPreferred?: boolean;
      findPharmacyClick?: boolean;
      searchAddress?: string;
    } = {}
  ): Promise<void> => {
    setLoading(true);
    const addressVal = params.searchAddress || addressTextFieldValue;
    if (zipcode) {
      window.scrollTo(0, 0);

      try {
        const trimmedAddress = addressVal.trim();
        const trimmedPharmName = pharmacyNameSearch.trim();
        const addressSearchIsZip = trimmedAddress.match(/^\d{5}$/gm);
        const pageNum =
          params.pageNumber !== undefined ? params.pageNumber : page;

        const pharmaciesRequest: PharmaciesRequest = {
          zipcode: addressSearchIsZip ? trimmedAddress : zipcode,
          page: pageNum,
          count: CONSTANTS.pharmacySelection.pageCount,
          name: trimmedPharmName,
          address: addressSearchIsZip ? "" : trimmedAddress,
          radius_miles: params.radius_miles || searchDistance,
        };

        if (isInNetwork && params.filterByPreferred) {
          pharmaciesRequest.preferred_only = params.filterByPreferred;
        }

        const response = isInNetwork
          ? await getInNetworkPharmacies(planId, pharmaciesRequest)
          : await getPharmacies(pharmaciesRequest);

        const {
          pharmacies: pharmaciesResponse,
          total_results,
          source_address,
          geocode_error,
        } = response;

        if (isInNetwork) {
          const {
            mail_order_network,
            mail_order_preferred,
          } = response as InNetworkPharmaciesResponse;
          setShouldMailOrderBeInResults(
            params.filterByPreferred ? mail_order_preferred : mail_order_network
          );
          dispatch({
            type: ActionType.SET_MAIL_ORDER_PHARMACY_NETWORK_STATUS,
            payload: {
              preferred: mail_order_preferred,
              inNetwork: mail_order_network,
            },
          });
        }

        dispatch({
          type: ActionType.SET_PHARMACIES,
          payload: updatePharmacyNetworkStatuses(
            pharmaciesResponse.features,
            savedPharmacies
          ),
        });

        setPharmacyResults({
          pharmacies: pharmaciesResponse,
          sourceAddress: source_address,
          geoError: geocode_error,
          totalResults: total_results,
        });

        setTotalPharmacies(total_results);
        setTotalPages(
          Math.ceil(total_results / CONSTANTS.pharmacySelection.pageCount)
        );
        const builtAddress = buildAddress(source_address);
        setAddressTextFieldValue(builtAddress);
        dispatch({
          type: ActionType.UPDATE_PHARMACY_ADDRESS,
          payload: builtAddress,
        });
        setPreviousNameSearch(pharmacyNameSearch);
        setPage(pageNum);

        setLoading(false);

        if (pharmaciesResponse.features.length === 0) {
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Find Plans - Add Pharmacy",
              event_label: `Error: ${t("pharm_selection.no_pharmacies")}`,
            },
          });
        }

        if (params.findPharmacyClick && !isInNetwork) {
          sendFindPharmacyGa4Event(total_results);
        }

        // If `calculateAverageDistance` is set to `true`, determine the rounded
        // average distance of all pharmacies, then recusively load pharmacies
        // for that search radius
        if (params.calculateAverageDistance) {
          const averageDistance = Math.ceil(
            pharmaciesResponse.features.reduce(
              (prev, curr) => prev + (curr.properties?.distance_miles || 0),
              0
            ) / pharmaciesResponse.features.length
          );

          let roundedDistance;

          if (averageDistance <= 1) {
            roundedDistance = 1;
          } else if (averageDistance <= 5) {
            roundedDistance = 5;
          } else if (averageDistance <= 10) {
            roundedDistance = 10;
          } else {
            roundedDistance = 25;
          }

          setSearchDistance(roundedDistance);
          // Recursively call this function with the updated search radius, now
          // set to the rounded, average distance
          // This recursion only happens once, because `calculateAverageDistance`
          // is not passed in again, below
          loadPharmacies({
            pageNumber: 0,
            radius_miles: roundedDistance,
            filterByPreferred: Boolean(params.filterByPreferred),
            searchAddress: builtAddress,
          });
          if (!isInNetwork) {
            dispatch({
              type: AnalyticsActionType.SEND_TEALIUM_EVENT,
              settings: {
                event_action: "Find Plans - Add Pharmacy",
                event_label: `Results UI: Filter by: ${roundedDistance} miles - Default`,
              },
            });
          }
        }
      } catch (e) {
        setLoading(false);
      }
    }
  };

  /**
   * toggleMailOrderPharmacy is called when the user check/uncheck mail-order pharmacy
   * @param add true to add mail-order pharmacy, false to remove mail-order pharmacy
   */
  const toggleMailOrderPharmacy = (add: boolean) => {
    // we need to dispatch two actions
    //  1. UPDATE_PHARMACY_TYPE
    //    - update the PharmacyType stored in AppStore.
    if (add) {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload: PharmacyType.MAIL_AND_RETAIL,
      });

      if (!isInNetwork) {
        // Add Mail Order Pharmacy - Regular Pharmacy
        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "Find Plans - Add Pharmacy",
            event_label: "Mail Order Pharmacy|Added",
          },
        });

        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.PHARMACY_SELECTION,
            mct_pharmacy_name: "Mail Order Pharmacy",
            mct_pharmacy_type: "mail",
          },
        });
      } else {
        // Add Mail Order Pharmacy - In-Network Pharmacy
        const selectedPharmacyCount = pharmacies.length + 1;

        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "Find Plans - Add Pharmacy",
            event_label: `added|mail order|Mail Order Pharmacy|${selectedPharmacyCount}`,
          },
        });
        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.IN_NETWORK_PHARMACY_SELECTED,
            mct_pharmacy_name: "Mail Order Pharmacy",
            mct_pharmacy_type: "mail",
            mct_in_network_pharmacy_finder: "yes",
            pharmacy_count: selectedPharmacyCount,
          },
        });
      }
    } else {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload: PharmacyType.RETAIL,
      });

      if (!isInNetwork) {
        // Remove Mail Order Pharmacy - Regular Pharmacy
        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "Find Plans - Add Pharmacy",
            event_label: "Mail Order Pharmacy|Removed",
          },
        });

        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.PHARMACY_REMOVED,
            mct_pharmacy_name: "Mail Order Pharmacy",
            mct_pharmacy_type: "mail",
          },
        });
      } else {
        // Remove Mail Order Pharmacy - In-Network Pharmacy
        const selectedPharmacyCount = pharmacies.length;

        dispatch({
          type: AnalyticsActionType.SEND_TEALIUM_EVENT,
          settings: {
            event_action: "Find Plans - Add Pharmacy",
            event_label: `removed|mail order|Mail Order Pharmacy|${selectedPharmacyCount}`,
          },
        });

        dispatch({
          type: AnalyticsActionType.SEND_GA4_EVENT,
          settings: {
            event_name: Ga4Event.PHARMACY_REMOVED,
            mct_pharmacy_name: "Mail Order Pharmacy",
            mct_pharmacy_type: "mail",
            mct_in_network_pharmacy_finder: "yes",
            pharmacy_count: selectedPharmacyCount,
          },
        });
      }
    }
    setMailOrder(add);
  };

  const togglePharmacy = (toggledFrom = ToggledFrom.toggledFromList) => (
    npi: string | number,
    pharmacyNumber = 0
  ): void => {
    const castedNpi = Number(npi).toString();
    if (pharmacyResults) {
      const npis = pharmacies.map(p => p.npi);
      const pharmacy = pharmacyResults.pharmacies.features.find(
        pgp => pgp.properties.npi === npi
      );
      const pharmacyName = pharmacy?.properties?.name || "";

      if (!npis.includes(castedNpi)) {
        if (
          CONSTANTS.pharmacySelection.max === pharmacies.length ||
          (pharmacies.length === CONSTANTS.pharmacySelection.max - 1 &&
            mailOrder)
        ) {
          setFooterMessage(
            fm(
              {
                id: "pharmacy_selection_page.max_selection_warning",
              },
              { count: CONSTANTS.pharmacySelection.max }
            )
          );
          return;
        }

        const pharmacy = pharmacyResults.pharmacies.features.find(
          feature => feature.properties?.npi === npi
        );

        dispatch({
          type: ActionType.ADD_PHARMACY,
          payload: pharmacy?.properties,
        });

        // Analytics
        if (!isInNetwork) {
          // Add Retail Pharmacy - Regular Pharmacy
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Find Plans - Add Pharmacy",
              event_label: `Retail|${pharmacyNumber}|${pharmacyName}|Added`,
            },
          });

          dispatch({
            type: AnalyticsActionType.SEND_GA4_EVENT,
            settings: {
              event_name: Ga4Event.PHARMACY_SELECTION,
              mct_pharmacy_name: pharmacyName,
              mct_pharmacy_type: "retail",
              search_result_count: pharmacyResults.totalResults,
              search_result_position: pharmacyNumber,
            },
          });
        } else {
          // Add Retail Pharmacy - In-Network Pharmacy
          const selectedPharmacyCount = mailOrder
            ? pharmacies.length + 2
            : pharmacies.length + 1;

          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Find Plans - Add Pharmacy",
              event_label: `added retail ${pharmacyName} ${selectedPharmacyCount}`,
            },
          });
          dispatch({
            type: AnalyticsActionType.SEND_GA4_EVENT,
            settings: {
              event_name: Ga4Event.IN_NETWORK_PHARMACY_SELECTED,
              mct_pharmacy_name: pharmacyName,
              mct_pharmacy_type: "retail",
              mct_in_network_pharmacy_finder: "yes",
              pharmacy_count: selectedPharmacyCount,
            },
          });
        }
      } else {
        // Remove Retail Pharmacy
        dispatch({ type: ActionType.DELETE_PHARMACY, payload: npi });

        // Analytics
        if (!isInNetwork) {
          // Remove Retail Pharmacy - Regular Pharmacy
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Find Plans - Add Pharmacy",
              event_label: `Retail|${pharmacyNumber}|${pharmacyName}|Removed`,
            },
          });
          dispatch({
            type: AnalyticsActionType.SEND_GA4_EVENT,
            settings: {
              event_name: Ga4Event.PHARMACY_REMOVED,
              mct_pharmacy_name: pharmacyName,
              mct_pharmacy_type: "retail",
            },
          });
        } else {
          // Remove Retail Pharmacy - In-Network Pharmacy
          const selectedPharmacyCount = mailOrder
            ? pharmacies.length
            : pharmacies.length - 1;

          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Find Plans - Add Pharmacy",
              event_label: `removed retail ${pharmacyName} ${selectedPharmacyCount}`,
            },
          });
          dispatch({
            type: AnalyticsActionType.SEND_GA4_EVENT,
            settings: {
              event_name: Ga4Event.PHARMACY_REMOVED,
              mct_pharmacy_name: pharmacyName,
              mct_pharmacy_type: "retail",
              mct_in_network_pharmacy_finder: "yes",
              pharmacy_count: selectedPharmacyCount,
            },
          });
        }

        if (toggledFrom === ToggledFrom.toggledFromTray) {
          dispatch({
            type: AnalyticsActionType.SEND_TEALIUM_EVENT,
            settings: {
              event_action: "Find Plans - Add Pharmacy",
              event_label: `Remove Pharmacy from Top 5`,
            },
          });
        }
      }
    }
  };

  const handleRemoveMailOrder = (): void => {
    toggleMailOrderPharmacy(false);
  };

  useEffect(() => {
    // reset the address search when zipcode changed.
    const newAddressVal = zipcode || "";
    setAddressTextFieldValue(newAddressVal);
    if (isInNetwork) {
      if (planId) {
        loadPharmacies({
          pageNumber: 0,
          radius_miles: DEFAULT_SEARCH_DISTANCE,
          calculateAverageDistance: true,
          searchAddress: newAddressVal,
        });
      }
    } else {
      loadPharmacies({
        pageNumber: 0,
        radius_miles: DEFAULT_SEARCH_DISTANCE,
        calculateAverageDistance: true,
        searchAddress: newAddressVal,
      });
    }
  }, [zipcode, planId, isInNetwork]);

  useEffect(() => {
    setMobileSearchDistance(showMobileFilter ? searchDistance : undefined);
  }, [showMobileFilter]);

  useEffect(() => {
    if (footerMessage !== "") {
      setFooterMessage("");
    }
  }, [footerMessage]);

  const inNetworkFilterAnalytics = (
    showPreferred: boolean,
    searchDistance: number
  ) => {
    if (isInNetwork) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Find Plans - Add Pharmacy",
          event_label: `Results UI: Filter by: Distance ${searchDistance} miles. Show only preferred in-network pharmacies: ${
            showPreferred ? "yes" : "no"
          }`,
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.RESULTS_FILTERED,
          click_text: "Show only preferred in-network pharmacies",
          radio_button_text: `Distance ${searchDistance} mile`,
          filter_action: showPreferred ? "applied" : "removed",
        },
      });
    }
  };

  const handlePreferredOnlyClick = (showPreferred: boolean) => {
    loadPharmacies({ pageNumber: 0, filterByPreferred: showPreferred });
    setShowOnlyPreferred(showPreferred);
    inNetworkFilterAnalytics(showPreferred, searchDistance);
  };

  const constructBackButtonRoute = () => {
    const { search } = location;
    if (isInNetwork) {
      return nextRoute
        ? (nextRoute as string)
        : `${routes.searchResults}${search}`;
    } else {
      return nextRoute ? (nextRoute as string) : routes.managePrescriptions;
    }
  };

  const handleOnBackButtonClick = () => {
    if (isInNetwork) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Find Plans - Add Pharmacy",
          event_label: "Back to plan details",
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.IN_NETWORK_PHARMACY_BACK_TO_PLAN_DETAILS,
          click_text: "Back to plan details",
        },
      });
    } else {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Find Plans - Add Pharmacy",
          event_label: "Back to drug selection",
        },
      });
    }
  };

  // heading is the shown on the page, i.e. <h1>.
  // title is the page title.
  const heading = isInNetwork
    ? t("pharm_selection.in_network.in_network_pharamcy_finder")
    : t("pharm_selection.choose_5");
  const yearInTitle = useYearInTitle(heading, state.year);
  const title = isInNetwork ? heading : yearInTitle;
  const constructBackLinkText = () => {
    if (nextRouteIsSummaryPage) {
      return (
        <>
          <HomeIcon className="ds-u-margin-right--1" />
          {t("summary_page.title")}
        </>
      );
    } else {
      return (
        <>
          <span className="fa fa-chevron-left ds-u-margin-right--1" />
          {isInNetwork
            ? t("pharm_selection.back_to_plan_details")
            : t("pharm_selection.back_to_drug_selection")}
        </>
      );
    }
  };

  return (
    <>
      <AppPage title={title}>
        <>
          {!hasMbpReferrerUri && (
            <Link
              to={constructBackButtonRoute()}
              onClick={handleOnBackButtonClick}
              className="SelectPharmaciesPage__backButton"
            >
              {constructBackLinkText()}
            </Link>
          )}
          {isInNetwork && plan && (
            <h2 className="ds-text-heading--lg ds-u-margin-bottom--0 e2e-plan-name-header">
              {plan.name ? getPlanName(plan, state.language) : ""}
            </h2>
          )}
          <FocusableHeader
            className={`ds-u-margin-bottom--1 ${
              isInNetwork ? "ds-u-margin-top--0" : "ds-u-margin-top--3"
            }`}
            text={heading}
          />
          {!isInNetwork && (
            <p className="SelectPharmaciesPage__desc">
              {t("pharm_selection.drug_costs")}
            </p>
          )}

          <form
            onSubmit={(e): void => {
              e.preventDefault();

              if (zipcode) {
                loadPharmacies({
                  pageNumber: 0,
                  radius_miles: searchDistance,
                  calculateAverageDistance: true,
                  filterByPreferred: showOnlyPreferred,
                  findPharmacyClick: true,
                });
              } else {
                sendFindPharmacyGa4Event(0);
              }
            }}
            className="ds-l-container ds-u-padding-x--0 ds-u-margin-x--0 ds-u-margin-top--1 ds-u-lg-margin-top--3"
          >
            <div className="ds-l-form-row ds-u-align-items--end">
              <div
                className={`ds-l-col--12 ${
                  isInNetwork ? "ds-l-lg-col--9" : "ds-l-lg-col--5"
                }`}
              >
                <TextField
                  className="SelectPharmaciesPage__zipInput e2e-pharm-zip"
                  fieldClassName="mct-u-max-width--full"
                  name="pharm-address"
                  value={addressTextFieldValue}
                  label={t("pharm_selection.pharm_address_input")}
                  onFocus={(): void => {
                    dispatch({
                      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                      settings: {
                        event_action: "Find Plans - Add Pharmacy",
                        event_label:
                          "UI Form: Enter your zip code or street address",
                      },
                    });
                  }}
                  onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                    setAddressTextFieldValue(event.target.value);
                  }}
                />
              </div>
              {!isInNetwork && (
                <div className="ds-l-col--12 ds-l-lg-col--4">
                  <TextField
                    label={t("pharm_selection.pharm_name_input")}
                    name="pharm-name"
                    value={pharmacyNameSearch}
                    className="SelectPharmaciesPage__pharmNameInput ds-u-margin-bottom--0"
                    fieldClassName="mct-u-max-width--full"
                    onFocus={(): void => {
                      dispatch({
                        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                        settings: {
                          event_action: "Find Plans - Add Pharmacy",
                          event_label: "UI Form: Name of pharmacy",
                        },
                      });
                    }}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                      setPharmacyNameSearch(event.target.value);
                    }}
                  />
                </div>
              )}
              <div className="ds-l-col--12 ds-l-lg-col--3 ds-u-margin-top--3 ds-u-lg-margin-top--0">
                <Button
                  type="submit"
                  variation="solid"
                  className="SelectPharmaciesPage__searchButton mct-u-min-width--full ds-u-padding--2"
                  onClick={(): void => {
                    dispatch({
                      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                      settings: {
                        event_action: "Find Plans - Add Pharmacy",
                        event_label: "CTA: Find Pharmacy",
                      },
                    });

                    dispatch({
                      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                      settings: {
                        button: {
                          buttonStyle: AnalyticsButtonStyle.PRIMARY,
                          buttonType: AnalyticsButtonType.SUBMIT,
                          text: t("pharm_selection.find_pharmacy"),
                        },
                      },
                    });
                  }}
                >
                  {t("pharm_selection.find_pharmacy")}
                </Button>
              </div>
            </div>
          </form>
          <ResultsContainer
            {...{
              handlePreferredOnlyClick,
              isInNetwork,
              isMailOrderInNetwork: mailOrderNetworkStatus.inNetwork,
              isMailOrderPreferred: mailOrderNetworkStatus.preferred,
              loading,
              loadPharmacies,
              mailOrder,
              mobileSearchDistance,
              page,
              pharmacyAnchorTarget,
              pharmacyResults,
              plan,
              previousNameSearch,
              searchDistance,
              setFooterMessage,
              setMobileSearchDistance,
              setPage,
              setSearchDistance,
              setShowMap,
              setShowMobileFilter,
              showMap,
              showMobileFilter,
              shouldMailOrderBeInResults,
              toggleMailOrderPharmacy,
              togglePharmacy,
              totalPages,
              totalPharmacies,
              inNetworkFilterAnalytics,
            }}
          />
          <LoadingMask loading={loading} delay={500} />
        </>
      </AppPage>
      <PharmacySelectionFooter
        selectionList={savedPharmacies}
        onRemovePharmacyFromSelection={togglePharmacy(
          ToggledFrom.toggledFromTray
        )}
        hasMailOrder={mailOrder}
        onRemoveMailOrder={handleRemoveMailOrder}
        pageLoading={loading}
        warningMessage={footerMessage}
        nextRoute={nextRoute}
        isInNetwork={isInNetwork}
        isMailOrderInNetwork={mailOrderNetworkStatus.inNetwork}
        isMailOrderPreferred={mailOrderNetworkStatus.preferred}
      />
    </>
  );
};
